/**
 * Home — the One Compass dataset-centric workspace (v2.5 Pillar 6, DC-1678).
 *
 * The left rail lists DATASETS (the source-grouping entity, DC-1676); selecting
 * one shows its analyses over time as timestamped cards on the right, with a
 * Re-analyze action that re-runs the saved source with no Databricks re-browse
 * (DC-1677) — it creates the pre-bound journey AND auto-launches the full
 * station set, so one click yields a running analysis (not an empty draft).
 * This replaces the v2.0 journey-flat list ("every analysis a
 * sibling") — re-analyses of the same source now nest under one dataset.
 *
 * Spec: docs/design/v2.5-dataset-centric-ux-spec.md · prototype:
 * docs/design/prototypes/dataset-home.html. The Trend view (toggle) ships with
 * DC-1679; this story is the Analyses view.
 */

import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApiClient } from '../hooks/useApiClient';
import { BRANDING } from '../config/branding';
import '../styles/home-workspace.css';

// The Trend view is an ApexCharts surface — lazy so the chart bundle stays out
// of the Home entry chunk and loads only when the Trend toggle is opened.
const TechnicalTrajectoryChart = React.lazy(() => import('../components/trajectory/TechnicalTrajectoryChart'));

// The four assessment stations a re-analysis runs — kept in lockstep with the
// first-analysis flow's REQUIRED_STATIONS (JourneyCreationForm.tsx). All are
// valid for both file and Databricks journeys via the per-station run route.
const REANALYZE_STATIONS = ['data_quality', 'ml_readiness', 'ontology_inference', 'live_checks'] as const;

// ============================================================================
// TYPES
// ============================================================================

type SourceType = 'databricks' | 'file' | 'other';

interface DatasetRow {
  id: string;
  name: string;
  source_type: SourceType;
  source_natural_key: string;
  analysis_count: number;
  last_analyzed_at: string | null;
}

interface AnalysisRow {
  journey_id: string;
  group_id: string;
  name: string;
  status: string;
  updated_at: string;
  // Per-station scores (0–100) from journey_stations.score; null = not run (≠ 0).
  dq: number | null;
  fair: number | null;
  ml: number | null;
  ontology: number | null;
}

interface ExecutionRiskData {
  risk_level: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL' | 'NOT_CONFIGURED';
  combined_factor: number;
}

// ============================================================================
// HELPERS
// ============================================================================

function relativeWhen(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function exactWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusChip(status: string): { label: string; cls: string } {
  switch (status) {
    case 'completed':
      return { label: 'Completed', cls: 'hw-chip--pass' };
    case 'in_progress':
    case 'active':
      return { label: 'Active', cls: 'hw-chip--warn' };
    case 'abandoned':
    case 'failed':
      return { label: status === 'failed' ? 'Failed' : 'Abandoned', cls: 'hw-chip--muted' };
    default:
      return { label: 'Draft', cls: 'hw-chip--muted' };
  }
}

/** 0.00 ≠ — : a null family is a gap, never a zero. (pg sends NUMERIC as string.) */
function score(value: number | null): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n)) : '—';
}

const SOURCE_LABEL: Record<SourceType, string> = { databricks: 'Databricks', file: 'File', other: 'Source' };

const DatabricksGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </svg>
);
const FileGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const SourceBadge: React.FC<{ type: SourceType }> = ({ type }) => (
  <span className={`ds-src ds-src--${type}`}>
    {type === 'databricks' ? <DatabricksGlyph /> : type === 'file' ? <FileGlyph /> : null}
    {SOURCE_LABEL[type]}
  </span>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const HomeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { authFetch } = useApiClient();

  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<AnalysisRow[]>([]);
  const [analysesLoading, setAnalysesLoading] = useState(false);
  const [analysesError, setAnalysesError] = useState<string | null>(null);

  const [risk, setRisk] = useState<ExecutionRiskData | null>(null);

  // filter bar
  const [sourceFilter, setSourceFilter] = useState<'all' | SourceType>('all');
  const [search, setSearch] = useState('');

  // rename + re-analyze interaction state (scoped to the selected dataset)
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [confirmReanalyze, setConfirmReanalyze] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [view, setView] = useState<'analyses' | 'trend'>('analyses');

  // ---- datasets + risk -------------------------------------------------------

  const loadDatasets = useCallback(async () => {
    try {
      setListLoading(true);
      setListError(null);
      const response = await authFetch('/api/datasets');
      if (!response.ok) throw new Error('Failed to load datasets');
      const data = await response.json();
      const rows: DatasetRow[] = data.datasets || [];
      setDatasets(rows);
      setSelectedId((current) => current ?? rows[0]?.id ?? null);
    } catch (err: unknown) {
      setListError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setListLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadDatasets();
    const loadRisk = async () => {
      try {
        const response = await authFetch('/api/organization/execution-risk');
        if (!response.ok) return;
        const data = await response.json();
        if (data.execution_risk) setRisk(data.execution_risk);
      } catch {
        // optional surface
      }
    };
    loadRisk();
  }, [loadDatasets, authFetch]);

  // ---- analyses for the selected dataset -------------------------------------

  const loadAnalyses = useCallback(
    async (datasetId: string) => {
      try {
        setAnalysesLoading(true);
        setAnalysesError(null);
        const response = await authFetch(`/api/datasets/${encodeURIComponent(datasetId)}/analyses`);
        if (!response.ok) throw new Error('Failed to load analyses');
        const data = await response.json();
        setAnalyses(data.analyses || []);
      } catch (err: unknown) {
        setAnalyses([]);
        setAnalysesError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setAnalysesLoading(false);
      }
    },
    [authFetch],
  );

  useEffect(() => {
    // reset per-dataset interaction state on selection change
    setEditingName(false);
    setConfirmReanalyze(false);
    setActionError(null);
    setView('analyses');
    if (selectedId) loadAnalyses(selectedId);
    else setAnalyses([]);
  }, [selectedId, loadAnalyses]);

  // ---- derived ---------------------------------------------------------------

  const selected = useMemo(() => datasets.find((d) => d.id === selectedId) ?? null, [datasets, selectedId]);

  const visibleDatasets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return datasets.filter(
      (d) =>
        (sourceFilter === 'all' || d.source_type === sourceFilter) &&
        (q === '' || d.name.toLowerCase().includes(q) || d.source_natural_key.toLowerCase().includes(q)),
    );
  }, [datasets, sourceFilter, search]);

  const totalAnalyses = useMemo(() => datasets.reduce((sum, d) => sum + (d.analysis_count || 0), 0), [datasets]);

  // ---- actions ---------------------------------------------------------------

  const handleReanalyze = useCallback(async () => {
    if (!selected) return;
    try {
      setReanalyzing(true);
      setActionError(null);

      // Resolve HOW to re-run (source + connection + warehouse pre-flight) before
      // creating anything. A stopped warehouse / unreachable Databricks fails here
      // with a fixable message instead of stranding the user on a dead journey.
      const planResp = await authFetch(`/api/datasets/${encodeURIComponent(selected.id)}/reanalyze-plan`);
      if (!planResp.ok) {
        const body = await planResp.json().catch(() => ({}));
        throw new Error(body.error || `Re-analysis failed (HTTP ${planResp.status})`);
      }
      const plan = await planResp.json();

      if (plan.source_type === 'databricks') {
        // Reuse the first-analysis experience: the import page drives the form
        // pre-bound + auto-running, so the staged streaming overlay is identical
        // to a brand-new Databricks analysis (the new journey's asset reuses the
        // same table location, so it auto-attaches to this dataset).
        navigate('/import/databricks', { state: { reanalyze: plan } });
        return;
      }

      // File / other: no streaming overlay flow, so create the pre-bound journey
      // and fire the per-station runs; the journey page polls and shows progress
      // (file station runs surface in_progress, unlike the synchronous Databricks
      // pushdown). asset_ids omitted -> the run route defaults to all journey assets.
      const response = await authFetch(`/api/datasets/${encodeURIComponent(selected.id)}/reanalyze`, {
        method: 'POST',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Re-analysis failed (HTTP ${response.status})`);
      }
      const body = await response.json();
      const groupId = body.group_id as string;
      for (const stationId of REANALYZE_STATIONS) {
        void authFetch(`/api/journey/${encodeURIComponent(groupId)}/station/${stationId}/run`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ timeout_ms: 300000 }),
        }).catch((stationErr: unknown) => {
          console.warn(`[reanalyze] failed to start ${stationId}:`, stationErr);
        });
      }
      navigate(`/journey/${groupId}`);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Re-analysis failed');
      setConfirmReanalyze(false);
    } finally {
      setReanalyzing(false);
    }
  }, [selected, authFetch, navigate]);

  const commitRename = useCallback(async () => {
    if (!selected) return;
    const next = nameDraft.trim();
    if (next === '' || next === selected.name) {
      setEditingName(false);
      return;
    }
    try {
      setActionError(null);
      const response = await authFetch(`/api/datasets/${encodeURIComponent(selected.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: next }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Rename failed');
      }
      setDatasets((prev) => prev.map((d) => (d.id === selected.id ? { ...d, name: next } : d)));
      setEditingName(false);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Rename failed');
    }
  }, [selected, nameDraft, authFetch]);

  // ---- render ----------------------------------------------------------------

  return (
    <div className="home-workspace" data-testid="home-workspace">
      <div className="hw-inner">
        <div className="hw-eyebrow">One Compass &middot; Home</div>
        <h1 className="hw-headline">Where your data <span className="hw-headline-accent">stands</span></h1>
        <p className="hw-subhead">Your datasets — pick one to see every analysis of it over time.</p>

        {/* stats row */}
        <div className="hw-stats">
          <div className="hw-stat">
            <span className="hw-stat-num">{datasets.length}</span>
            <span className="hw-stat-lbl">datasets</span>
          </div>
          <div className="hw-stat">
            <span className="hw-stat-num">{totalAnalyses}</span>
            <span className="hw-stat-lbl">analyses</span>
          </div>
          {risk && risk.risk_level !== 'NOT_CONFIGURED' && (
            <div className="hw-stat">
              <span className="hw-stat-num">{Math.round(risk.combined_factor * 100)}%</span>
              <span className="hw-stat-lbl">execution risk &middot; {risk.risk_level.toLowerCase()}</span>
            </div>
          )}
        </div>

        <div className="hw-workspace">
          {/* master: datasets rail */}
          <div>
            <p className="hw-section-label">Datasets</p>

            {/* DC-1678 follow-up: the dataset-centric rewrite dropped the only
                initial-analysis CTA (re-analyze covers existing datasets, but a
                first analysis had no entry point). Restored at the TOP of the
                rail so it's always reachable — including the empty state. */}
            <Link className="hw-new-analysis hw-new-analysis--top" to="/import" data-testid="new-analysis">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New analysis
            </Link>

            <div className="ds-filter">
              <div className="ds-seg" role="group" aria-label="Filter datasets by source">
                {(['all', 'databricks', 'file'] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={sourceFilter === opt}
                    onClick={() => setSourceFilter(opt)}
                  >
                    {opt === 'all' ? 'All' : SOURCE_LABEL[opt]}
                  </button>
                ))}
              </div>
              <input
                className="ds-search"
                type="search"
                placeholder="Search datasets…"
                aria-label="Search datasets"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {listLoading && <p className="hw-muted">Loading datasets…</p>}
            {listError && (
              <p className="hw-error" role="alert">
                {listError} <button type="button" className="ds-retry" onClick={loadDatasets}>Retry</button>
              </p>
            )}

            {!listLoading && !listError && visibleDatasets.length === 0 && (
              <p className="hw-muted">
                {datasets.length === 0
                  ? `No datasets yet — import a Databricks table or upload a file to run your first ${BRANDING.productName} analysis.`
                  : 'No datasets match your filter.'}
              </p>
            )}

            <div className="ds-list">
              {visibleDatasets.map((d) => (
                <button
                  type="button"
                  key={d.id}
                  className={`ds-row${d.id === selectedId ? ' is-selected' : ''}`}
                  aria-current={d.id === selectedId}
                  onClick={() => setSelectedId(d.id)}
                  data-testid={`ds-row-${d.id}`}
                >
                  <span className="ds-name">{d.name}</span>
                  <span className="ds-meta">
                    <SourceBadge type={d.source_type} />
                    <span className="ds-count">
                      {d.analysis_count} {d.analysis_count === 1 ? 'analysis' : 'analyses'}
                    </span>
                    <span className="ds-when">{relativeWhen(d.last_analyzed_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* detail: selected dataset's analyses */}
          <div className="hw-detail" data-testid="hw-detail">
            {!selected && !listLoading && (
              <p className="hw-muted hw-detail-pad">Select a dataset on the left.</p>
            )}

            {selected && (
              <>
                <div className="ds-head">
                  <div className="ds-head-main">
                    {editingName ? (
                      <input
                        className="ds-rename"
                        aria-label="Rename dataset"
                        autoFocus
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') setEditingName(false);
                        }}
                      />
                    ) : (
                      <h2 className="ds-head-title">
                        {selected.name}
                        <button
                          type="button"
                          className="ds-pencil"
                          aria-label="Rename dataset"
                          onClick={() => {
                            setNameDraft(selected.name);
                            setEditingName(true);
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                          </svg>
                        </button>
                      </h2>
                    )}
                    <div className="ds-source">
                      <code>{selected.source_natural_key}</code>
                      <SourceBadge type={selected.source_type} />
                    </div>
                  </div>

                  <div className="ds-head-actions">
                    <div className="ds-seg ds-viewseg" role="group" aria-label="View">
                      <button type="button" aria-pressed={view === 'analyses'} onClick={() => setView('analyses')}>
                        Analyses
                      </button>
                      <button type="button" aria-pressed={view === 'trend'} onClick={() => setView('trend')}>
                        Trend
                      </button>
                    </div>
                    {confirmReanalyze ? (
                      <div className="ds-confirm" role="group" aria-label="Confirm re-analysis">
                        <span className="ds-confirm-q">Run a fresh analysis?</span>
                        <button
                          type="button"
                          className="hw-btn hw-btn--primary"
                          disabled={reanalyzing}
                          onClick={handleReanalyze}
                        >
                          {reanalyzing ? 'Starting…' : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          className="hw-btn hw-btn--ghost"
                          disabled={reanalyzing}
                          onClick={() => setConfirmReanalyze(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="hw-btn hw-btn--primary"
                        data-testid="ds-reanalyze"
                        onClick={() => setConfirmReanalyze(true)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M21 2v6h-6" />
                          <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                          <path d="M3 22v-6h6" />
                          <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                        </svg>
                        Re-analyze
                      </button>
                    )}
                  </div>
                </div>

                {confirmReanalyze && !actionError && (
                  <p className="ds-confirm-note">Its current scores stay in the dataset’s history as a prior analysis.</p>
                )}
                {actionError && (
                  <p className="hw-error" role="alert">{actionError}</p>
                )}

                {view === 'analyses' ? (
                  <>
                {/* analyses */}
                {analysesLoading && <p className="hw-muted hw-detail-pad">Loading analyses…</p>}
                {analysesError && (
                  <p className="hw-error" role="alert">
                    {analysesError}{' '}
                    <button type="button" className="ds-retry" onClick={() => selectedId && loadAnalyses(selectedId)}>
                      Retry
                    </button>
                  </p>
                )}

                {!analysesLoading && !analysesError && analyses.length === 0 && (
                  <p className="hw-muted hw-detail-pad">No analyses recorded for this dataset yet.</p>
                )}

                <div className="an-stack">
                  {analyses.map((a, idx) => {
                    const chip = statusChip(a.status);
                    return (
                      <button
                        type="button"
                        key={a.journey_id}
                        className="an-card"
                        onClick={() => navigate(`/journey/${a.group_id}`)}
                        data-testid={`an-card-${a.group_id}`}
                      >
                        <span className="an-when">
                          {relativeWhen(a.updated_at)}
                          <small>{exactWhen(a.updated_at)}</small>
                        </span>
                        <span className="an-mid">
                          <span className="an-title">{a.name}</span>
                          <span className="an-scores">
                            <span className="an-sc"><b>{score(a.dq)}</b>DQ</span>
                            <span className="an-sc"><b>{score(a.fair)}</b>FAIR</span>
                            <span className="an-sc"><b>{score(a.ml)}</b>ML</span>
                            <span className="an-sc"><b>{score(a.ontology)}</b>Ontology</span>
                          </span>
                        </span>
                        <span className="an-tail">
                          {idx === 0 && <span className="hw-chip hw-chip--pass an-latest">Latest</span>}
                          <span className={`hw-chip ${chip.cls}`}>{chip.label}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                  </>
                ) : (
                  <Suspense fallback={<p className="hw-muted hw-detail-pad">Loading trend…</p>}>
                    <div className="ds-trend">
                      <TechnicalTrajectoryChart datasetId={selected.id} />
                    </div>
                  </Suspense>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeDashboard;
